/**
 * PortalDashboard - Enhanced Customer Dashboard
 * Features: Quick-Book, Social Proof, Weekly Progress, Motivational Quote
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar, Clock, Package, Quote, Zap,
  TrendingUp, Flame, Dumbbell, Apple, Scale,
  ChevronRight, Users, Star, RotateCcw, Building2
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import OnboardingWalkthrough from './OnboardingWalkthrough';
import MobilePromoBanner from './MobilePromoBanner';
import { formatStat } from '../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LOCALE_MAP = { de: 'de-CH', en: 'en-GB', fr: 'fr-CH', it: 'it-CH', es: 'es-ES', pt: 'pt-PT', sk: 'sk-SK', ru: 'ru-RU' };

const dayOfYearIndex = (len) => {
  if (!len) return 0;
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return dayOfYear % len;
};

const PortalDashboard = ({ darkTheme, customer, setActiveTab }) => {
  const { t, i18n } = useTranslation();
  const quotesData = t('portal.dashboard.quotes', { returnObjects: true });
  const quoteList = Array.isArray(quotesData) ? quotesData : [];
  const quote = quoteList.length ? quoteList[dayOfYearIndex(quoteList.length)] : { text: '', author: '' };
  const [stats, setStats] = useState({ upcomingAppointments: 0, activePackages: 0, nextAppointment: null });
  const [widgets, setWidgets] = useState(null);
  const [loading, setLoading] = useState(true);
  // Quick Re-Purchase (Iter 285): zeigt 1-Tap Karte für den letzten Kauf
  const [lastPurchase, setLastPurchase] = useState(null);
  const [repeatBusy, setRepeatBusy] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };

      const [appointmentsRes, packagesRes, widgetsRes, lastPurchaseRes] = await Promise.all([
        axios.get(`${API}/portal/appointments/upcoming`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/packages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/insights/dashboard-widgets`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/portal/shop/last-purchase`, { headers }).catch(() => ({ data: null })),
      ]);

      const appointments = Array.isArray(appointmentsRes.data) ? appointmentsRes.data : [];
      const packages = Array.isArray(packagesRes.data) ? packagesRes.data : [];

      const now = new Date();
      const thirtyDays = new Date(now.getTime() + 30 * 86400000);
      const upcoming = appointments.filter(a => {
        const d = new Date(a.start_time);
        return a.status === 'scheduled' && d > now && d <= thirtyDays;
      });
      const next = upcoming.sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0] || null;

      setStats({
        upcomingAppointments: upcoming.length,
        activePackages: packages.filter(p => p.remaining_units > 0).length,
        nextAppointment: next
      });
      setWidgets(widgetsRes.data);
      // Last purchase nur zeigen wenn Shop-Order (volume) — wiederholbar.
      // Subscriptions zeigen wir NICHT, weil die ggf. noch laufen.
      const lp = lastPurchaseRes.data;
      if (lp && lp.has_last_purchase && lp.source === 'shop_order') {
        setLastPurchase(lp);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString(
    LOCALE_MAP[(i18n.language || 'de').split('-')[0]] || 'de-CH',
    { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }
  );

  // Quick Re-Purchase Handler — re-uses public shop checkout für 1-Tap
  const handleRepeatPurchase = async () => {
    if (!lastPurchase || repeatBusy) return;
    setRepeatBusy(true);
    try {
      const token = localStorage.getItem('customerToken');
      const headers = { Authorization: `Bearer ${token}` };
      // Get customer email
      const meRes = await axios.get(`${API}/portal/me`, { headers });
      const email = meRes.data?.email;
      if (!email) throw new Error('Keine E-Mail');
      const res = await axios.post(`${API}/public/shop/checkout`, {
        item_type: lastPurchase.item_type,
        item_id: lastPurchase.item_id,
        customer_email: email,
        studio_id: lastPurchase.studio_id || null,
        quantity: 1,
      });
      // Öffne Payrexx in neuem Fenster (eingeloggter Kunde, kein Modal nötig)
      if (res.data?.payment_link) {
        window.open(res.data.payment_link, '_blank');
      }
    } catch (e) {
      // Fallback: leite zum Shop weiter
      window.location.href = '/portal#shop';
    } finally {
      setRepeatBusy(false);
    }
  };

  const accent = darkTheme.accent;
  const progress = widgets?.weekly_progress;
  const popular = widgets?.popular_services || [];
  const socialProof = widgets?.social_proof;

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <OnboardingWalkthrough />
      <MobilePromoBanner />

      {/* Welcome + Progress Score Row */}
      <div className="pt-4 pb-1" data-testid="portal-dashboard-welcome">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.18em] mb-2 inline-block"
          style={{ color: accent, borderBottom: `1.5px solid ${accent}`, paddingBottom: '3px' }}
        >
          {t('portal.dashboard.welcome_badge')}
        </p>
        <h1
          className="text-2xl md:text-3xl font-medium tracking-tight"
          style={{ color: darkTheme.text, fontFamily: "'Outfit', sans-serif", textTransform: 'none' }}
        >
          {t('portal.dashboard.hello', { name: customer?.first_name || t('portal.dashboard.guest') })}
        </h1>
        <p className="text-sm mt-1.5" style={{ color: darkTheme.textMuted }}>
          {t('portal.dashboard.motivation_sub')}
        </p>
      </div>

      {/* Quick Re-Purchase Card (Iter 285) — 1-Tap für letzten Kauf */}
      {lastPurchase && (
        <button
          className="w-full text-left rounded-xl border transition-colors flex items-center gap-4 p-4"
          style={{ backgroundColor: darkTheme.cardBg, borderColor: `${accent}55` }}
          onClick={handleRepeatPurchase}
          data-testid="portal-quick-repurchase"
        >
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: darkTheme.bg }}>
            <RotateCcw className="h-5 w-5" style={{ color: accent }} strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.16em] font-semibold mb-1" style={{ color: accent }}>
              {t('portal.dashboard.repurchase_kicker')}
            </p>
            <p className="text-sm font-medium truncate" style={{ color: darkTheme.text }}>
              {lastPurchase.label}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: darkTheme.textMuted }}>
              <span className="font-medium" style={{ color: accent }}>
                CHF {Number(lastPurchase.price || 0).toFixed(2)}
              </span>
              {lastPurchase.studio_name && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {lastPurchase.studio_name}
                </span>
              )}
            </div>
          </div>
          <span
            className="shrink-0 px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ backgroundColor: repeatBusy ? darkTheme.border : accent, color: '#0a0a0a', opacity: repeatBusy ? 0.6 : 1 }}
          >
            {repeatBusy ? '…' : t('portal.dashboard.repurchase_now')}
          </span>
        </button>
      )}
      {/* Quote + Weekly Progress */}
      <div className="flex flex-col md:flex-row gap-4">
        <div
          className="md:flex-[2] rounded-xl p-5 flex items-start gap-4"
          style={{ backgroundColor: darkTheme.cardBg, border: `1px solid ${darkTheme.border}` }}
          data-testid="portal-dashboard-quote-card"
        >
          <Quote className="h-5 w-5 shrink-0 mt-0.5" style={{ color: accent }} strokeWidth={1.75} />
          <div>
            <p className="text-base md:text-lg font-medium italic leading-relaxed mb-2" style={{ color: darkTheme.text }}>
              "{quote.text}"
            </p>
            <p className="text-xs" style={{ color: darkTheme.textMuted }}>— {quote.author}</p>
          </div>
        </div>
        {progress && (
          <div
            className="md:flex-1 rounded-xl p-5 flex items-center gap-4"
            style={{ backgroundColor: darkTheme.cardBg, border: `1px solid ${darkTheme.border}` }}
            data-testid="portal-progress-score"
          >
            <div className="relative w-12 h-12 shrink-0">
              <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                <circle cx="18" cy="18" r="15" fill="none" stroke={darkTheme.border} strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15" fill="none"
                  stroke={accent}
                  strokeWidth="3"
                  strokeDasharray={`${progress.progress_score * 0.94} 94`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold" style={{ color: accent }}>
                {progress.progress_score}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium" style={{ color: darkTheme.textMuted }}>{t('portal.dashboard.weekly_progress')}</p>
              <p className="text-[13px] mt-0.5" style={{ color: darkTheme.text }}>
                {t('portal.dashboard.progress_summary', { trainings: progress.trainings, meals: progress.nutrition_entries })}
              </p>
              {progress.streak_days > 0 && (
                <span className="flex items-center gap-1 text-xs mt-1" style={{ color: accent }}>
                  <Flame className="w-3 h-3" /> {t('portal.dashboard.streak', { days: progress.streak_days })}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick-Book: Popular Services */}
      {popular.length > 0 && (
        <div data-testid="portal-quick-book">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-2.5" style={{ color: darkTheme.textMuted }}>
            {t('portal.dashboard.book_now')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {popular.map((svc, i) => (
              <button
                key={svc.service_id || i}
                className="text-left rounded-xl p-4 flex items-center gap-3 transition-colors group"
                style={{ backgroundColor: darkTheme.cardBg, border: `1px solid ${darkTheme.border}` }}
                onClick={() => setActiveTab('booking')}
                onMouseEnter={e => (e.currentTarget.style.borderColor = `${accent}55`)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = darkTheme.border)}
                data-testid={`portal-quick-book-${i}`}
              >
                <Zap className="w-4 h-4 shrink-0" style={{ color: darkTheme.textMuted }} strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: darkTheme.text }}>
                    {svc.name}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: darkTheme.textMuted }}>
                    {i === 0 ? t('portal.dashboard.most_popular') : t('portal.dashboard.popular')}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: accent }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Clock} label={t('portal.dashboard.stat_next')}
          value={stats.nextAppointment ? stats.nextAppointment.service_name : t('portal.dashboard.stat_next_none')}
          sub={stats.nextAppointment ? formatDate(stats.nextAppointment.start_time) : t('portal.dashboard.stat_next_book')}
          accent={accent} theme={darkTheme}
          onClick={() => setActiveTab('appointments')}
          testId="portal-stat-next"
        />
        <StatCard
          icon={Calendar} label={t('portal.dashboard.stat_appointments')}
          value={formatStat(stats.upcomingAppointments)}
          sub={t('portal.dashboard.stat_appointments_sub')}
          accent={accent} theme={darkTheme}
          onClick={() => setActiveTab('appointments')}
          testId="portal-stat-appointments"
        />
        <StatCard
          icon={Package} label={t('portal.dashboard.stat_packages')}
          value={formatStat(stats.activePackages)}
          sub={t('portal.dashboard.stat_packages_sub')}
          accent={accent} theme={darkTheme}
          onClick={() => setActiveTab('packages')}
          testId="portal-stat-packages"
        />
        {progress && (
          <StatCard
            icon={TrendingUp} label={t('portal.dashboard.stat_week')}
            value={`${formatStat(progress.trainings)} / ${formatStat(progress.nutrition_entries)}`}
            sub={t('portal.dashboard.stat_week_sub')}
            accent={accent} theme={darkTheme}
            onClick={() => setActiveTab('nutrition')}
            testId="portal-stat-weekly"
          />
        )}
      </div>

      {/* Quick Actions - flat shortcuts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portal-dashboard-quick-actions">
        {[
          { icon: Calendar, label: t('portal.dashboard.action_book'), sub: t('portal.dashboard.action_book_sub'), tab: 'booking' },
          { icon: Dumbbell, label: t('portal.dashboard.action_training'), sub: t('portal.dashboard.action_training_sub'), tab: 'fitness' },
          { icon: Apple, label: t('portal.dashboard.action_nutrition'), sub: t('portal.dashboard.action_nutrition_sub'), tab: 'nutrition' },
          { icon: Scale, label: t('portal.dashboard.action_body'), sub: t('portal.dashboard.action_body_sub'), tab: 'fitness' },
        ].map((action) => (
          <button
            key={action.tab + action.label}
            onClick={() => setActiveTab(action.tab)}
            className="text-left rounded-xl p-4 transition-colors group"
            style={{ backgroundColor: darkTheme.cardBg, border: `1px solid ${darkTheme.border}` }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = `${accent}55`)}
            onMouseLeave={e => (e.currentTarget.style.borderColor = darkTheme.border)}
            data-testid={`portal-action-${action.tab}`}
          >
            <action.icon className="w-4 h-4 mb-3" style={{ color: darkTheme.textMuted }} strokeWidth={1.75} />
            <p className="text-sm font-medium" style={{ color: darkTheme.text }}>{action.label}</p>
            <p className="text-[11px] mt-0.5" style={{ color: darkTheme.textMuted }}>{action.sub}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent, theme, onClick, testId }) => (
  <button
    className="text-left w-full rounded-xl p-4 transition-colors"
    style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.border}` }}
    onClick={onClick}
    onMouseEnter={e => (e.currentTarget.style.borderColor = `${accent}55`)}
    onMouseLeave={e => (e.currentTarget.style.borderColor = theme.border)}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-3.5 h-3.5" style={{ color: theme.textMuted }} strokeWidth={1.5} />
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: theme.textMuted }}>{label}</span>
    </div>
    <p className="text-lg font-semibold truncate" style={{ color: theme.text }}>{value}</p>
    <p className="text-xs mt-1 truncate" style={{ color: theme.textMuted }}>{sub}</p>
  </button>
);

export default PortalDashboard;
